import React, { useState, useMemo, useEffect } from 'react';
import '../../styles/Modal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import useNotification from '../../hooks/useNotification';
import DataService from '../../services/DataService';
import QuoteService from '../../services/QuoteService';
import TimeStamp from '../../utils/TimeStamp';

const CloneQuoteModal = ({ isActive, onClose, sourceQuoteCustomId, onCloned }) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [originalProjectName, setOriginalProjectName] = useState('');
  const [originalAccountId, setOriginalAccountId] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const { showNotification } = useNotification();
  const currentDate = useMemo(() => TimeStamp.fromDate(new Date()), []);

  useEffect(() => {
    if (!isActive) return;
    (async () => {
      try {
        const accts = await DataService.getAll('accounts', 0, 1000);
        if (originalAccountId) {
          setAccounts(accts.filter(a => a.id !== originalAccountId));
        } else {
          setAccounts(accts);
        }
      } catch (e) {
        console.error('Erreur chargement comptes', e);
        showNotification('Impossible de charger la liste des comptes', 'error');
      }
    })();
  }, [isActive, showNotification, originalAccountId]);

  useEffect(() => {
    if (!isActive || !sourceQuoteCustomId) return;
  
    (async () => {
      try {
        // Récupérer le devis source puis l'opportunité pour obtenir le nom du projet d'origine
        const sourceQuote = await DataService.getQuoteByCustomId(sourceQuoteCustomId);
        let baseName = sourceQuoteCustomId;
        let baseAccountId = null;
        if (sourceQuote && sourceQuote.opportunityId) {
          const opp = await DataService.getById('opportunities', sourceQuote.opportunityId);
          if (opp && opp.name) baseName = opp.name;
          if (opp && opp.accountId) baseAccountId = opp.accountId;
        }
        const defaultName = `${baseName} - Copie`;
        setOriginalProjectName(baseName);
        setProjectName(defaultName);
        if (baseAccountId) {
          setOriginalAccountId(baseAccountId);
          // si la liste des comptes est déjà chargée, filtrer le compte d'origine
          setAccounts(prev => (prev && prev.length > 0) ? prev.filter(a => a.id !== baseAccountId) : prev);
        }
      } catch (error) {
        console.error('Erreur récupération nom du projet source', error);
        setOriginalProjectName(sourceQuoteCustomId);
        setProjectName(`${sourceQuoteCustomId} - Copie`);
      }
    })();
  }, [isActive, sourceQuoteCustomId]);

  const handleConfirm = async (e) => {
    e && e.preventDefault();
    if (!selectedAccountId) {
      showNotification('Veuillez sélectionner un compte cible', 'warning');
      return;
    }
    if (!projectName || projectName.trim() === '') {
      showNotification('Le nom du projet est requis', 'warning');
      return;
    }

    setIsCloning(true);
    try {
      const result = await QuoteService.cloneToAccount(sourceQuoteCustomId, selectedAccountId, {
        opportunityName: projectName,
        quoteName: projectName
      });
      showNotification('Clonage terminé', 'success');
      if (onCloned) onCloned(result);
      // laisser un court délai pour voir la notification
      setTimeout(() => onClose(), 600);
    } catch (error) {
      console.error('Erreur clonage', error);
      showNotification(`Erreur lors du clonage : ${error.message || error}`, 'error');
    } finally {
      setIsCloning(false);
    }
  };

  if (!isActive) return null;

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={() => { if (!isCloning) onClose(); }}></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">Copie de : {originalProjectName}</p>
          <button className="delete" aria-label="close" onClick={() => { if (!isCloning) onClose(); }}></button>
        </header>
        <section className="modal-card-body">
          <form onSubmit={handleConfirm}>
            <div className="table-container">
              <table className="table is-fullwidth is-striped is-hoverable mb-0">
                <tbody>
                  <tr>
                    <td style={{ width: '200px' }}>
                      <label className="label">Projet</label>
                    </td>
                    <td>
                      <input
                        className="input"
                        type="text"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="Nom du projet"
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label className="label">Compte cible</label>
                    </td>
                    <td>
                      <div className="select is-fullwidth">
                        <select value={selectedAccountId || ''} onChange={e => setSelectedAccountId(Number(e.target.value))}>
                          <option value="">-- Sélectionner un compte --</option>
                          {accounts.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.name}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label className="label">Date</label>
                    </td>
                    <td>
                      <input className="input" type="text" value={currentDate.toString()} disabled />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
          <p className="help">Les factures et paiements liés au devis source ne seront pas copiés.</p>
        </section>
        <footer className="modal-card-foot">
          <button className="button" onClick={() => { if (!isCloning) onClose(); }} disabled={isCloning}>Annuler</button>
          <button className="button is-primary" onClick={handleConfirm} disabled={isCloning}>
            {isCloning ? <span className="icon"><FontAwesomeIcon icon={faSpinner} spin /></span> : 'Cloner'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CloneQuoteModal;